/**
 * 游戏列表 Hook
 * 支持分页、筛选、排序
 */

import { useState, useCallback } from 'react';
import { useQuery, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { gameAdapter } from '../adapters';
import type { ListGamesParams } from '../api/types';

/**
 * 基础游戏列表
 */
export function useGames(params?: ListGamesParams) {
  return useQuery({
    queryKey: ['games', params],
    queryFn: async () => {
      const result = await gameAdapter.listGames(params);
      return result;
    },
  });
}

/**
 * 无限滚动游戏列表
 */
export function useInfiniteGames(initialParams: ListGamesParams = {}) {
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery({
    queryKey: ['games', 'infinite', params],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await gameAdapter.listGames({
        ...params,
        page: pageParam,
        limit: 20,
      });
      return result;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data.hasMore) return undefined;
      return allPages.length + 1;
    },
    initialPageParam: 1,
  });

  // 更新筛选参数
  const updateParams = useCallback((newParams: Partial<ListGamesParams>) => {
    setParams(prev => ({ ...prev, ...newParams }));
  }, []);

  // 重置筛选
  const resetParams = useCallback(() => {
    setParams(initialParams);
  }, [initialParams]);

  // 扁平化游戏列表
  const games = query.data?.pages.flatMap(page => page.data.games) ?? [];

  return {
    ...query,
    games,
    params,
    updateParams,
    resetParams,
  };
}

/**
 * 游戏详情
 * 返回游戏对象而不是适配器结果
 */
export function useGameDetail(idOrSlug: string | undefined) {
  return useQuery({
    queryKey: ['game', idOrSlug],
    queryFn: async () => {
      if (!idOrSlug) throw new Error('Game ID is required');
      const result = await gameAdapter.getGameDetail(idOrSlug);
      return result.data.game; // 返回 Game 对象
    },
    enabled: !!idOrSlug,
  });
}

/**
 * 统计追踪 - 浏览
 */
export function useTrackView() {
  return useMutation({
    mutationFn: async (gameId: number) => {
      await gameAdapter.trackView(gameId.toString());
    },
  });
}

/**
 * 统计追踪 - 点击
 */
export function useTrackClick() {
  return useMutation({
    mutationFn: async ({ gameId, platformId }: { gameId: number; platformId: string }) => {
      await gameAdapter.trackClick(gameId.toString(), platformId);
    },
  });
}

/**
 * 统计追踪（兼容旧版）
 */
export function useTracking() {
  const trackView = useCallback(async (gameId: string) => {
    await gameAdapter.trackView(gameId);
  }, []);

  const trackClick = useCallback(async (gameId: string, platformId: string) => {
    await gameAdapter.trackClick(gameId, platformId);
  }, []);

  return { trackView, trackClick };
}
